import "./timelineAgeView.scss";

import equal from "fast-deep-equal";
import { isNil } from "lodash";
import PropTypes from "prop-types";
import React, { PureComponent } from "react";
import { connect } from "react-redux";

import { withCharacterContext } from "../../components/character/CharacterContext";
import { withContainerContext } from "../../components/container/ContainerContext";
import { withXScrollPositionContext } from "../../components/scrollable/ScrollPositionContext";
import { appStateSelectors } from "../../modules/appState";
import { calendarSelectors } from "../../modules/definition/calendar";
import { immutableRecords, PropTypeShapes } from "../../types";
import selectors from "./selectors";

// import debugUtils from "../../utils/debugUtils";

class TimelineAgeView extends PureComponent {
  constructor(props, context) {
    super(props, context);

    this.state = { stickyLabel: null };

    this.updateStickyLabel = this.updateStickyLabel.bind(this);

    this._liveScrollListeners = {
      onLiveScrollStart: this.onLiveScrollStart.bind(this),
      onLiveScrollUpdate: this.onLiveScrollUpdate.bind(this),
      onLiveScrollStop: this.onLiveScrollStop.bind(this)
    };

    this._horizontalScrollerActive = false;
    this._domNode = null;
    this._liveLeft = 0;
    this._ageLabels = null;
  }

  componentDidMount() {
    this.props.xScrollActions.registerForLiveUpdates(this._liveScrollListeners);
  }

  // componentWillReceiveProps(nextProps) {
  //   debugUtils.logPropChanges("TimelineAgeView", this.props, nextProps, true);
  // }

  componentDidUpdate(prevProps) {
    if (
      (!equal(this.props.startDate.timestamp, prevProps.startDate.timestamp) ||
        (this.props.endDate &&
          prevProps.endDate &&
          !equal(this.props.endDate.timestamp, prevProps.endDate.timestamp)) ||
        !equal(this.props.xScrollPosition.zoom, prevProps.xScrollPosition.zoom)) &&
      !this._horizontalScrollerActive
    ) {
      this.updateStickyLabel();
    }
  }

  componentWillUnmount() {
    this.props.xScrollActions.unregisterForLiveUpdates(this._liveScrollListeners);
  }

  onLiveScrollStart(horizontalScroller, _verticalScroller) {
    if (horizontalScroller) {
      this._horizontalScrollerActive = true;
    }
  }

  onLiveScrollUpdate(scrollLeft, _scrollTop) {
    // Update both liveTop and liveLeft as needed, but we only need to applyTransform when
    // we are changing top.
    if (!isNil(scrollLeft) && scrollLeft !== this._liveLeft) {
      this._liveLeft = scrollLeft;
      this.updateStickyLabel();
    }
  }

  onLiveScrollStop(horizontalScroller, _verticalScroller) {
    if (horizontalScroller) {
      this._horizontalScrollerActive = false;
    }
  }

  getAgeLabels(absoluteLeft, absoluteRight) {
    // Get absolute positions with padding so we can do the manual scroll thing.
    const scrollLeft = absoluteLeft + 1000;
    const scrollRight = absoluteRight - 1000;

    const characterWidthMap = this.props.characterContext.getWidthMap();

    const timelineAgeLabels = selectors.getTimelineAgeLabels(
      this.props.calendar,
      this.props.viewId,
      this.props.xScrollPosition.zoom,
      scrollLeft,
      scrollRight,
      this.props.fontSize,
      this.props.startDate,
      this.props.endDate,
      characterWidthMap
    );

    return timelineAgeLabels;
  }

  renderAgeLabels(virtualOffset) {
    if (this._ageLabels) {
      let backgroundStyle = {};
      let leftX = 0;

      let labels = this._ageLabels.map(t => {
        const translateX = t.pixel - virtualOffset;
        if (t.isStart) {
          leftX = translateX;
          backgroundStyle.left = translateX + "px";
          backgroundStyle.width = this.props.xScrollPosition.canvas.range - translateX + "px";
        }
        if (t.isEnd) {
          backgroundStyle.width = translateX - leftX + "px";
        }
        return (
          <div
            key={t.label}
            className={`ageLabel ${t.isEnd ? "ageLabelEnd" : ""}`}
            style={{ transform: `translateX(${translateX}px)` }}
          >
            {t.label}
          </div>
        );
      });

      if (labels.length) {
        labels.unshift(<div key="timelineAgeBackground" className="timelineAgeBackground" style={backgroundStyle} />);
      }
      return labels;
    }
  }

  updateStickyLabel() {
    const virtualOffset = this.props.xScrollPosition.canvas.min;
    const liveMin = this._liveLeft - virtualOffset;

    if (this._ageLabels) {
      let stickyLabel = null;

      for (let i = 0; i < this._ageLabels.length; i++) {
        const label = this._ageLabels[i];
        const labelLeft = label.pixel - virtualOffset;
        if (labelLeft < liveMin && !label.isEnd) {
          stickyLabel = `(${label.label})`;
        } else if (labelLeft < liveMin + 175) {
          stickyLabel = null;
          break;
        }
      }

      this.setState({
        stickyLabel: stickyLabel
      });
    }
  }

  render() {
    // Calculate left and right bounds
    const viewport = this.props.xScrollPosition.viewport;
    const virtualOffset = this.props.xScrollPosition.canvas.min;
    const unitScale = this.props.xScrollPosition.zoom;

    this._ageLabels = unitScale ? this.getAgeLabels(viewport.min, viewport.max) : null;

    const style = {
      left: `${this.props.containerRect.left}px`,
      top: `${this.props.containerRect.top}px`,
      height: `${this.props.containerRect.height}px`,
      overflow: "hidden",
      position: "fixed"
    };

    return (
      <div className="timelineAgeView">
        <div className="timelineAgeCanvas">
          {this.renderAgeLabels(virtualOffset)}
          <div style={style} ref={node => (this._domNode = node)} className="timelineAgeStickyLabel">
            {this.state.stickyLabel ? <span>{this.state.stickyLabel}</span> : null}
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state, _props) => {
  return {
    calendar: calendarSelectors.getCalendar(state.present),
    fontSize: appStateSelectors.getFontSize(state.present)
  };
};

const mapDispatchToProps = null;

TimelineAgeView.propTypes = {
  fontSize: PropTypes.number.isRequired,
  calendar: PropTypeShapes.calendar.isRequired,
  viewId: PropTypes.string.isRequired,
  startDate: PropTypeShapes.dateRelated,
  endDate: PropTypeShapes.dateRelated,

  // From withScrollPositionContext
  xScrollPosition: PropTypes.instanceOf(immutableRecords.ScrollPositionRecord).isRequired,
  xScrollActions: PropTypeShapes.scrollActions.isRequired,

  // From containerContext
  containerRect: PropTypeShapes.flatRect.isRequired,

  // From character context
  characterContext: PropTypes.object.isRequired
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withXScrollPositionContext(withContainerContext(withCharacterContext(TimelineAgeView))));
